import {
  RaidPlanCast,
  RaidPlanEncounterAssignmentAbility,
} from 'app/frontend/types/custom/RaidPlan'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'
import {
  AssignmentViewMode,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getAssignmentScopeIndexes,
  resolveAssignmentViewMode,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from '../../TeamPlans/components/assignmentTimeline'
import {
  EncounterAssignmentEntityOverride,
  EventPlanEncounterAssignmentOverrides,
  EventPlanOverridesByPlanId,
  getEncounterAssignmentAbilityHidden,
  getEncounterAssignmentEntityOverride,
  getPlanEncounterAssignmentOverrides,
  setActorOverrideIgnored,
  setCastTargetEntityOverride,
  setEncounterAssignmentEntityOverride,
} from '../components/eventActivityPlanOverrides'
import {
  EventActivityTimelineResolverIssue,
  ResolvedAssignmentEntity,
  ResolvedAssignmentParticipant,
  ResolvedTimelineActor,
} from '../components/eventActivityTimelineResolver'

type ApplyEventActivityPlanOverridesInput = {
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>
  abilities: RaidPlanEncounterAssignmentAbility[]
  encounterRows: EncounterAbilityRow[]
  selectedParticipants: ResolvedAssignmentParticipant[]
  encounterAssignmentOverrides?: EventPlanEncounterAssignmentOverrides
}

const toCastIds = (
  ability: RaidPlanEncounterAssignmentAbility,
  castIndexes: number[],
  assignmentGroups: number,
) =>
  castIndexes
    .map(
      (castIndex) =>
        normalizeAssignmentCast(ability.casts?.[castIndex], assignmentGroups)
          .id,
    )
    .filter((castId): castId is string => Boolean(castId))

export const applyEventActivityPlanOverrides = ({
  resolvedByScopeBoxKey,
  abilities,
  encounterRows,
  selectedParticipants,
  encounterAssignmentOverrides,
}: ApplyEventActivityPlanOverridesInput) => {
  if (!encounterAssignmentOverrides) return resolvedByScopeBoxKey
  const selectedParticipantByCharacterId = selectedParticipants.reduce<
    Record<number, ResolvedAssignmentParticipant>
  >((acc, participant) => {
    acc[participant.characterId] = participant
    return acc
  }, {})
  const nextResolvedByScopeBoxKey = { ...resolvedByScopeBoxKey }

  abilities.forEach((ability, abilityIndex) => {
    const row = encounterRows[abilityIndex]
    if (!row) return
    const assignmentGroups = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      ability.assignment_groups,
    )
    const viewMode: AssignmentViewMode = resolveAssignmentViewMode(
      ability.view_mode,
      row.entries.length,
      ability.assignment_type,
      ability.nsrt_interrupt_module,
    )
    const customGrouping = getEffectiveAbilityCustomGrouping(
      ability.spell_id,
      ability.custom_grouping,
    )
    const scopes = getAssignmentScopeIndexes(
      row.entries,
      viewMode,
      customGrouping,
      ability.assignment_type,
    )

    scopes.forEach((scope) => {
      const castIds = toCastIds(ability, scope.castIndexes, assignmentGroups)
      if (!castIds.length) return
      const representativeCast = normalizeAssignmentCast(
        ability.casts?.[scope.castIndexes[0]],
        assignmentGroups,
      )

      for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
        const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
        const resolvedEntities = nextResolvedByScopeBoxKey[key]
        if (!resolvedEntities?.length) continue
        const box = normalizeAssignmentBox(representativeCast.boxes[boxIndex])

        nextResolvedByScopeBoxKey[key] = resolvedEntities.map(
          (resolvedEntity, entityIndex) => {
            const sourceEntity =
              box.entities[entityIndex] ?? resolvedEntity.sourceEntity
            const override = getEncounterAssignmentEntityOverride({
              encounterAssignments: encounterAssignmentOverrides,
              abilityKey: row.abilityKey,
              castIds,
              boxId: box.id,
              entityId: sourceEntity?.id,
            })
            if (!override) return resolvedEntity
            if (override.kind === 'participant') {
              const participant =
                selectedParticipantByCharacterId[override.characterId]
              if (!participant) return resolvedEntity
              return {
                kind: 'participant',
                participant,
                sourceEntity: resolvedEntity.sourceEntity,
                isOverridden: true,
              }
            }
            if (override.kind === 'ignored') {
              return {
                kind: 'ignored',
                sourceEntity: resolvedEntity.sourceEntity,
                isOverridden: true,
              }
            }
            return toAggregateOverrideEntity(override)
          },
        )
      }
    })
  })

  return nextResolvedByScopeBoxKey
}

const toAggregateOverrideEntity = (
  override: Extract<EncounterAssignmentEntityOverride, { kind: 'aggregate' }>,
): ResolvedAssignmentEntity => ({
  kind: 'aggregate',
  sourceEntity: override.entity,
  isOverridden: true,
})

const scopeBoxKeyMatchesAbility = (scopeBoxKey: string, abilityKey: string) =>
  scopeBoxKey === abilityKey || scopeBoxKey.startsWith(`${abilityKey}:`)

export const getEventActivityUnresolvedIssues = (
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>,
  hiddenAbilityKeys?: Iterable<string>,
): EventActivityTimelineResolverIssue[] => {
  const hiddenKeys = hiddenAbilityKeys ? new Set(hiddenAbilityKeys) : null
  const issues: EventActivityTimelineResolverIssue[] = []
  Object.entries(resolvedByScopeBoxKey).forEach(
    ([scopeBoxKey, resolvedEntities]) => {
      if (
        hiddenKeys &&
        Array.from(hiddenKeys).some((abilityKey) =>
          scopeBoxKeyMatchesAbility(scopeBoxKey, abilityKey),
        )
      ) {
        return
      }
      resolvedEntities.forEach((entity) => {
        if (entity.issue) issues.push(entity.issue)
      })
    },
  )
  return issues
}

type UnresolvedAssignmentEntityRef = {
  abilityKey: string
  castIds: string[]
  boxId: string
  entityId: string
}

const collectUnresolvedAssignmentEntityRefs = ({
  resolvedByScopeBoxKey,
  linkedPlanAssignments,
  hiddenAbilityKeys,
}: {
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>
  linkedPlanAssignments: Array<{
    abilityKey: string
    ability: RaidPlanEncounterAssignmentAbility
    row: EncounterAbilityRow
  }>
  hiddenAbilityKeys: Iterable<string>
}): UnresolvedAssignmentEntityRef[] => {
  const hiddenKeys = new Set(hiddenAbilityKeys)
  const refs: UnresolvedAssignmentEntityRef[] = []

  linkedPlanAssignments.forEach(({ ability, row }) => {
    if (hiddenKeys.has(row.abilityKey)) return
    const assignmentGroups = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      ability.assignment_groups,
    )
    const viewMode: AssignmentViewMode = resolveAssignmentViewMode(
      ability.view_mode,
      row.entries.length,
      ability.assignment_type,
      ability.nsrt_interrupt_module,
    )
    const customGrouping = getEffectiveAbilityCustomGrouping(
      ability.spell_id,
      ability.custom_grouping,
    )
    const scopes = getAssignmentScopeIndexes(
      row.entries,
      viewMode,
      customGrouping,
      ability.assignment_type,
    )

    scopes.forEach((scope) => {
      const castIds = toCastIds(ability, scope.castIndexes, assignmentGroups)
      if (!castIds.length) return
      const representativeCast = normalizeAssignmentCast(
        ability.casts?.[scope.castIndexes[0]],
        assignmentGroups,
      )

      for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
        const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
        const resolvedEntities = resolvedByScopeBoxKey[key]
        if (!resolvedEntities?.length) continue
        const box = normalizeAssignmentBox(representativeCast.boxes[boxIndex])
        const boxId = box.id
        if (!boxId) continue

        resolvedEntities.forEach((resolvedEntity, entityIndex) => {
          if (!resolvedEntity.issue) return
          const sourceEntity =
            box.entities[entityIndex] ?? resolvedEntity.sourceEntity
          const entityId = sourceEntity?.id
          if (!entityId) return
          refs.push({
            abilityKey: row.abilityKey,
            castIds,
            boxId,
            entityId,
          })
        })
      }
    })
  })

  return refs
}

const collectUnresolvedCastTargetRefs = ({
  resolvedActors,
  generalCasts,
  resolvedByCastId,
}: {
  resolvedActors: ResolvedTimelineActor[]
  generalCasts: RaidPlanCast[]
  resolvedByCastId: Record<string, ResolvedAssignmentEntity[]>
}) => {
  const allCasts = [
    ...resolvedActors.flatMap((resolvedActor) => resolvedActor.actor.casts),
    ...generalCasts,
  ]
  return allCasts.flatMap((cast) => {
    const resolvedTargets = resolvedByCastId[cast.id] ?? []
    const targetRows = (cast.targets ?? []).flatMap((group, groupIndex) =>
      group.map((_, entityIndex) => ({
        entityId: `${groupIndex}:${entityIndex}`,
      })),
    )
    return targetRows.flatMap((row, flatIndex) => {
      if (!resolvedTargets[flatIndex]?.issue) return []
      return [{ castId: cast.id, entityId: row.entityId }]
    })
  })
}

type IgnoreAllUnresolvedLinkedPlanIssuesInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  resolvedActors: ResolvedTimelineActor[]
  linkedPlanAssignments: Array<{
    abilityKey: string
    ability: RaidPlanEncounterAssignmentAbility
    row: EncounterAbilityRow
  }>
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>
  resolvedByCastId: Record<string, ResolvedAssignmentEntity[]>
  generalCasts?: RaidPlanCast[]
}

export const ignoreAllUnresolvedLinkedPlanIssues = ({
  planOverrides,
  planId,
  resolvedActors,
  linkedPlanAssignments,
  resolvedByScopeBoxKey,
  resolvedByCastId,
  generalCasts = [],
}: IgnoreAllUnresolvedLinkedPlanIssuesInput): EventPlanOverridesByPlanId => {
  const encounterAssignmentOverrides = getPlanEncounterAssignmentOverrides(
    planOverrides,
    planId,
  )
  const hiddenAbilityKeys = linkedPlanAssignments
    .filter((assignment) =>
      getEncounterAssignmentAbilityHidden({
        encounterAssignments: encounterAssignmentOverrides,
        abilityKey: assignment.abilityKey,
      }),
    )
    .map((assignment) => assignment.abilityKey)

  const ignoredOverride: EncounterAssignmentEntityOverride = {
    kind: 'ignored',
  }
  let nextPlanOverrides = planOverrides

  resolvedActors.forEach((resolvedActor) => {
    if (resolvedActor.participant || resolvedActor.isIgnored === true) return
    nextPlanOverrides = setActorOverrideIgnored({
      planOverrides: nextPlanOverrides,
      planId,
      actorUuid: resolvedActor.actor.uuid,
      ignored: true,
    })
  })

  collectUnresolvedAssignmentEntityRefs({
    resolvedByScopeBoxKey,
    linkedPlanAssignments,
    hiddenAbilityKeys,
  }).forEach((ref) => {
    nextPlanOverrides = setEncounterAssignmentEntityOverride({
      planOverrides: nextPlanOverrides,
      planId,
      abilityKey: ref.abilityKey,
      castIds: ref.castIds,
      boxId: ref.boxId,
      entityId: ref.entityId,
      override: ignoredOverride,
    })
  })

  collectUnresolvedCastTargetRefs({
    resolvedActors,
    generalCasts,
    resolvedByCastId,
  }).forEach((ref) => {
    nextPlanOverrides = setCastTargetEntityOverride({
      planOverrides: nextPlanOverrides,
      planId,
      castId: ref.castId,
      entityId: ref.entityId,
      override: ignoredOverride,
    })
  })

  return nextPlanOverrides
}
